import { useCallback } from 'react';
import { frameOfStoredVideo, videoPosterKey } from '../../utils/videoPoster';
import { useLocalFolderAccess } from './quickEditor/useLocalFolderAccess';
import { useMediaThumbnails, type ThumbnailRequest } from './quickEditor/useMediaThumbnails';

export interface PosterVideo {
  id: string;
  url: string;
  label?: string;
}

interface PosterOptions {
  userId?: string;
  ownerUserId?: string;
  workspaceName?: string;
}

/**
 * A picture for each video, made from the copy on this device when there is one. While the
 * browser still has to allow the local folder again, nothing is read from the cloud (the video
 * may be on disk); pictures that could not be made are tried again once access is granted.
 * Pictures are shared with the editor's media list and kept in this browser.
 */
export const useVideoPosters = (videos: PosterVideo[], { userId, ownerUserId, workspaceName }: PosterOptions) => {
  const { access, request } = useLocalFolderAccess(userId);
  const allowDownload = access !== null && access.state !== 'prompt';
  const keyOf = useCallback((video: PosterVideo) => videoPosterKey(video.url, `poster-${video.id}`).key, []);
  // Nothing is fetched before it is known whether the local folder can be read
  const requests: ThumbnailRequest[] = access === null ? [] : videos.map(video => ({
    ...videoPosterKey(video.url, `poster-${video.id}`),
    make: () => frameOfStoredVideo(video.url, { userId, ownerUserId, workspaceName, label: video.label, allowDownload }),
  }));
  const pictures = useMediaThumbnails(requests, access?.state);
  return {
    posterOf: (video: PosterVideo): string | undefined => pictures[keyOf(video)],
    /** The folder is there but the browser must allow it again before more pictures can be made. */
    folderLocked: access?.state === 'prompt',
    allowFolder: request,
  };
};
